import { useEffect, useRef, useState } from "react";
import { FiAlertTriangle } from "react-icons/fi";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { OfferTab } from "@/features/worksheets/components/tabs/offer-tab";
import { getDetermination } from "@/features/workflows/api";
import type { WorkflowDetermination } from "@/features/workflows/types";

interface Props {
  caseId: string;
  workflowId: string;
  /** Bubbles the computed determination up so the rail chip + gating update. */
  onDetermination: (det: WorkflowDetermination) => void;
  /** Choose IA and advance to the terms step (persists selected_form). */
  onChoosePlan: (form: "433-H" | "433-D") => void;
  /** Switch to the Offer-in-Compromise workflow for the same case. */
  onChooseOic: () => void;
}

/**
 * Step 6 — Summary of Alternatives. Reuses the Tier 2.2 `OfferTab` (IA/OIC
 * determination, tax-year include/exclude toggle, payment-schedule + IA-worksheet
 * dialogs, CSED warnings), and adds the workflow seam: it caches the
 * determination onto the workflow (rail chip), blocks on an expired CSED, and
 * the "choose plan to file" action that routes IA → step 7 (OIC → 03-14 seam).
 */
export function SummaryStep({
  caseId,
  workflowId,
  onDetermination,
  onChoosePlan,
  onChooseOic,
}: Props) {
  const [det, setDet] = useState<WorkflowDetermination | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    getDetermination(caseId, workflowId)
      .then((d) => {
        if (!mounted.current) return;
        setDet(d);
        onDetermination(d);
      })
      .catch(() => undefined);
    return () => {
      mounted.current = false;
    };
    // onDetermination is stable from the page; caseId/workflowId drive refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, workflowId]);

  const expired = det?.expired === true;
  const noTaxDue = det?.available === true && (det?.totalDueCents ?? 0) <= 0;

  return (
    <div className="grid gap-6">
      {expired && (
        <Alert variant="destructive">
          <FiAlertTriangle />
          <AlertTitle>Collection statute expired — installment agreement blocked</AlertTitle>
          <AlertDescription>
            One or more included tax years is past its CSED. Remove or correct those
            years before generating IA forms.
          </AlertDescription>
        </Alert>
      )}

      {/* The full determination panel (Tier 2.2). */}
      <OfferTab caseId={caseId} />

      {/* Comparison seams — OIC/CNC are recommendation-only in this workflow. */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Card className="text-muted-foreground gap-1 p-4 text-sm">
          <p className="text-foreground font-semibold">Offer in Compromise</p>
          <p>Shown for comparison above. Generating an OIC is the 03-14 workflow.</p>
        </Card>
        <Card className="text-muted-foreground gap-1 p-4 text-sm">
          <p className="text-foreground font-semibold">Currently Not Collectible</p>
          <p>Recommendation only — not generated by the IA workflow.</p>
        </Card>
      </div>

      <Card className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="text-sm">
          <p className="text-foreground font-semibold">Choose plan to file</p>
          <p className="text-muted-foreground">
            Proceed with the Installment Agreement using Form 433-H or 433-D.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            disabled={expired || noTaxDue}
            onClick={() => onChoosePlan("433-D")}
          >
            File with 433-D
          </Button>
          <Button
            disabled={expired || noTaxDue}
            onClick={() => onChoosePlan("433-H")}
            title={expired ? "Blocked — expired CSED" : undefined}
          >
            File with 433-H
          </Button>
          <Button variant="ghost" onClick={onChooseOic}>
            File OIC instead
          </Button>
        </div>
      </Card>
    </div>
  );
}
